import { isAxiosError } from 'axios';

interface ServerErrorResponse {
  message?: string;
  Message?: string;
  validationErrors?: string[];
  ValidationErrors?: string[];
  title?: string;
  /** Observer ProblemDetails: flat string[]. ASP.NET Core ModelState: Record<string, string[]>. */
  errors?: string[] | Record<string, string[]>;
  // Bedrock envelope fields
  code?: string | null;
  success?: boolean;
}

/**
 * Returns the Bedrock machine-readable error code from a Bedrock-shaped
 * response (e.g. "auth.invalid_credentials"). Returns null when the response
 * is not Bedrock-shaped or the call did not fail with a code.
 */
export function getServerErrorCode(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data as ServerErrorResponse | undefined;
  return data?.code ?? null;
}

/**
 * Extracts validation error messages from an AxiosError response.
 * Handles both custom API error format and ASP.NET Core ProblemDetails format.
 */
export function extractServerErrors(error: unknown): string[] {
  if (!isAxiosError(error)) return [];
  const data = error.response?.data as ServerErrorResponse | undefined;
  if (!data) return [];

  // Custom validationErrors array format (both cases)
  if (data.validationErrors?.length) return data.validationErrors;
  if (data.ValidationErrors?.length) return data.ValidationErrors;

  // Message field (both cases)
  if (data.message) return [data.message];
  if (data.Message) return [data.Message];

  // Observer ProblemDetails format: errors as flat string[]
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors as string[];
  }

  // ASP.NET Core ModelState ProblemDetails format: errors as Record<field, string[]>
  if (data.errors && typeof data.errors === 'object') {
    const errorMessages: string[] = [];
    for (const [_field, messages] of Object.entries(data.errors)) {
      if (Array.isArray(messages)) {
        errorMessages.push(...messages);
      }
    }
    if (errorMessages.length > 0) return errorMessages;
  }

  // Title field (ASP.NET Core ProblemDetails)
  if (data.title) return [data.title];

  return [];
}
